/**
 * PickerCollectionクラスは、特定のコレクションとその製品を検索するための機能を提供します。
 */
class PickerCollection {
  constructor() {

  }

  /**
   * 指定されたハンドルに基づいてコレクションを検索します。
   * 
   * @param {string} handle - コレクションのハンドル。
   * @returns {Promise<Object>} コレクションの詳細を含むPromiseオブジェクト。
   * @property {Object} collection - コレクションの情報。
   * @property {number} collection.id - コレクションのID。
   * @property {string} collection.title - コレクションのタイトル。
   * @property {string} collection.handle - コレクションのハンドル。
   * @property {string} collection.description - コレクションの説明。
   * @property {string} collection.published_at - コレクションの公開日時。
   * @property {string} collection.updated_at - コレクションの更新日時。
   * @property {Object} collection.image - コレクションの画像情報。
   * @property {number} collection.image.id - 画像のID。
   * @property {string} collection.image.created_at - 画像の作成日時。
   * @property {string} collection.image.src - 画像のURL。
   * @property {string|null} collection.image.alt - 画像の代替テキスト。
   * @property {number} collection.products_count - コレクション内の製品数。
   * @throws {Error} フェッチに失敗した場合にエラーをスローします。
   */
  async searchCollection(handle) {
    const response = await fetch(`/collections/${handle}.json`)

    if (!response.ok) {
      throw new Error(`Failed to fetch collection: ${response.statusText}`)
    }

    const { collection } = await response.json()

    return collection
  }

  /**
   * 指定されたハンドルに基づいてコレクション内の製品を検索します。
   * 
   * @param {string} handle - コレクションのハンドル。
   * @returns {Promise<Object[]>} コレクション内の製品の詳細を含むPromiseオブジェクトの配列。
   * @property {Object} product - 製品の情報。
   * @property {number} product.id - 製品のID。
   * @property {string} product.title - 製品のタイトル。
   * @property {string} product.handle - 製品のハンドル。
   * @property {string} product.body_html - 製品の説明。
   * @property {string} product.published_at - 製品の公開日時。
   * @property {string} product.created_at - 製品の作成日時。
   * @property {string} product.updated_at - 製品の更新日時。
   * @property {string} product.vendor - 製品のベンダー。
   * @property {string} product.product_type - 製品のタイプ。
   * @property {string[]} product.tags - 製品のタグ。
   * @property {Object[]} product.variants - 製品のバリアント情報。
   * @property {number} product.variants.id - バリアントのID。
   * @property {string} product.variants.title - バリアントのタイトル。
   * @property {string} product.variants.sku - バリアントのSKU。
   * @property {boolean} product.variants.requires_shipping - 配送が必要かどうか。
   * @property {boolean} product.variants.taxable - 課税対象かどうか。
   * @property {boolean} product.variants.available - バリアントが利用可能かどうか。
   * @property {string} product.variants.price - バリアントの価格。
   * @property {Object[]} product.images - 製品の画像情報。
   * @property {number} product.images.id - 画像のID。
   * @property {string} product.images.src - 画像のURL。
   * @property {number} product.images.width - 画像の幅。
   * @property {number} product.images.height - 画像の高さ。
   * @property {Object[]} product.options - 製品のオプション情報。
   * @property {string} product.options.name - オプションの名前。
   * @property {string[]} product.options.values - オプションの値。
   * @throws {Error} フェッチに失敗した場合にエラーをスローします。
   */
  async searchCollectionProducts(handle, limit = 30) {
    const response = await fetch(`/collections/${handle}/products.json?limit=${limit}`)

    if (!response.ok) {
      throw new Error(`Failed to fetch collection products: ${response.statusText}`)
    }

    const { products } = await response.json()

    return products
  }
}
